import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PerfusionInfo } from '../../lib/api/perfusionInfoApi';

interface PerfusionState {
    perfusions: PerfusionInfo[];
    isLoading: boolean;
    error: string | null;
    lastFetched: number | null;
    // Cache duration in milliseconds (5 minutes)
    cacheExpiry: number;
}

const initialState: PerfusionState = {
    perfusions: [],
    isLoading: false,
    error: null,
    lastFetched: null,
    cacheExpiry: 5 * 60 * 1000, // 5 minutes
};

const perfusionSlice = createSlice({
    name: 'perfusion',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setPerfusions: (state, action: PayloadAction<PerfusionInfo[]>) => {
            state.perfusions = action.payload;
            state.lastFetched = Date.now();
            state.error = null;
        },
        // Action to mark data as stale and force a refresh
        setDataStale: (state) => {
            state.lastFetched = null;
        },
    },
});

// Action creators
export const {
    setLoading,
    setError,
    setPerfusions,
    setDataStale,
} = perfusionSlice.actions;

// Selectors
export const selectAllPerfusions = (state: { perfusion: PerfusionState }) => state.perfusion.perfusions;
export const selectPerfusionLoading = (state: { perfusion: PerfusionState }) => state.perfusion.isLoading;
export const selectPerfusionError = (state: { perfusion: PerfusionState }) => state.perfusion.error;
const selectLastFetched = (state: { perfusion: PerfusionState }) => state.perfusion.lastFetched;
const selectCacheExpiry = (state: { perfusion: PerfusionState }) => state.perfusion.cacheExpiry;

// Derived selectors
export const selectPerfusionById = (state: { perfusion: PerfusionState }, perfusionId: string) =>
    state.perfusion.perfusions.find(p => p.perfusion_id === perfusionId);

export const selectPerfusionsByDate = (state: { perfusion: PerfusionState }, date: string) =>
    state.perfusion.perfusions.filter(perfusion => {
        if (!perfusion.perfusion_date) return false;
        const perfusionDate = new Date(perfusion.perfusion_date).toISOString().split('T')[0];
        return perfusionDate === date;
    });

export const selectPerfusionsByMonth = (state: { perfusion: PerfusionState }, month: string, includeNeighbors: boolean = true) => {
    if (!includeNeighbors) {
        return state.perfusion.perfusions.filter(perfusion => {
            if (!perfusion.perfusion_date) return false;
            const perfusionMonth = new Date(perfusion.perfusion_date).toISOString().slice(0, 7);
            return perfusionMonth === month;
        });
    }

    // Include neighboring months for better UX
    const targetDate = new Date(month + '-01');
    const startOfPrevMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() - 1, 1);
    const endOfNextMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 2, 0);

    return state.perfusion.perfusions.filter(perfusion => {
        if (!perfusion.perfusion_date) return false;
        const perfusionDate = new Date(perfusion.perfusion_date);
        return perfusionDate >= startOfPrevMonth && perfusionDate <= endOfNextMonth;
    });
};

export const selectPerfusionsByExperimentDesign = (state: { perfusion: PerfusionState }, experimentDesignId: number) =>
    state.perfusion.perfusions.filter(perfusion => perfusion.experiment_design_id === experimentDesignId);

export const selectPerfusionStatusCounts = (state: { perfusion: PerfusionState }) => {
    const statusCounts: Record<number, Record<string, number>> = {};

    state.perfusion.perfusions.forEach(perfusion => {
        if (perfusion.experiment_design_id && perfusion.status) {
            const designId = perfusion.experiment_design_id;
            const status = perfusion.status;

            if (!statusCounts[designId]) {
                statusCounts[designId] = {};
            }

            if (!statusCounts[designId][status]) {
                statusCounts[designId][status] = 0;
            }

            statusCounts[designId][status]++;
        }
    });

    return statusCounts;
};

const selectUniqueStatuses = (state: { perfusion: PerfusionState }) => {
    const statuses = new Set<string>();
    state.perfusion.perfusions.forEach(perfusion => {
        if (perfusion.status) {
            statuses.add(perfusion.status);
        }
    });
    return Array.from(statuses).sort();
};

// Helper selector to check if data is stale
export const selectIsDataStale = (state: { perfusion: PerfusionState }) => {
    const { lastFetched, cacheExpiry } = state.perfusion;
    if (!lastFetched) return true;
    return Date.now() - lastFetched > cacheExpiry;
};

export default perfusionSlice.reducer; 